import React, { useState, useEffect } from 'react';
import ModalCreate from '../../../Components/Common/Modal';
import DeleteModal from '../../../Components/Common/DeleteModal';
import {
  addQuickReply,
  type QuickReplyItem,
  editQuickReply,
  removeQuickReply,
} from '../utils/quick-replies';
import {Label} from "reactstrap";

interface QuickRepliesManagerModalProps {
  show: boolean;
  replies: QuickReplyItem[];
  onClose: () => void;
  onUpdate: (updatedReplies: QuickReplyItem[]) => void;
}

const QuickRepliesManagerModal: React.FC<QuickRepliesManagerModalProps> = ({
  show,
  replies,
  onClose,
  onUpdate
}) => {
  const [loading, setLoading] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [newTrigger, setNewTrigger] = useState('');
  const [newResponse, setNewResponse] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);

  useEffect(() => {
    if (show) {
      setEditingIndex(null);
      setNewTrigger('');
      setNewResponse('');
    }
  }, [show]);

  const handleEdit = (index: number) => {
    const reply = replies[index];
    setEditingIndex(index);
    setNewTrigger(reply.trigger);
    setNewResponse(reply.response);
  };

  const handleSave = async () => {
    if (!newTrigger.trim() || !newResponse.trim()) return;

    try {
      setLoading(true);
      
      if (editingIndex !== null) {
        // Edit existing reply
        const reply = replies[editingIndex];
        const updatedReply = await editQuickReply(reply.id, newTrigger.trim(), newResponse.trim());
        const updatedReplies = [...replies];
        updatedReplies[editingIndex] = updatedReply;
        onUpdate(updatedReplies);
      } else {
        // Add new reply
        const newReply = await addQuickReply(newTrigger.trim(), newResponse.trim());
        const updatedReplies = [...replies, newReply];
        onUpdate(updatedReplies);
      }

      setEditingIndex(null);
      setNewTrigger('');
      setNewResponse('');
      setShowForm(false);
    } catch (error) {
      console.error('Error saving reply:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (index: number) => {
    setDeleteIndex(index);
    setDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (deleteIndex === null) return;
    
    try {
      setLoading(true);
      const reply = replies[deleteIndex];
      await removeQuickReply(reply.id);
      const updatedReplies = replies.filter((_, i) => i !== deleteIndex);
      onUpdate(updatedReplies);
      
      if (editingIndex === deleteIndex) {
        setEditingIndex(null);
        setNewTrigger('');
        setNewResponse('');
      }
      
      setDeleteModal(false);
      setDeleteIndex(null);
    } catch (error) {
      console.error('Error deleting reply:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModal(false);
    setDeleteIndex(null);
  };

  const handleCancel = () => {
    setEditingIndex(null);
    setNewTrigger('');
    setNewResponse('');
    setShowForm(false);
  };



  if (!show) return null;

  // Sider modal (panel deslizante desde la derecha)
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="d-block"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.4)',
        zIndex: 1050,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          height: '100%',
          width: '480px',
          maxWidth: '100%',
          background: '#fff',
          boxShadow: '0 0 24px rgba(0,0,0,0.2)',
          display: 'flex',
          flexDirection: 'column',
          transform: 'translateX(0)',
          transition: 'transform 0.25s ease',
          borderTopLeftRadius: '12px',
          borderBottomLeftRadius: '12px',
        }}
      >
        {/* HEADER */}
        <div className="px-4 py-3 bg bg-primary border-bottom d-flex justify-content-between align-items-center" style={{borderTopLeftRadius: '12px'}}>
          <div className="fw-semibold text-white">Respuestas rápidas</div>

          <button
            type="button"
            className="btn p-1"
            style={{ background: 'transparent', border: 'none' }}
            onClick={onClose}
            aria-label="Cerrar"
          >
            <i className="ri-close-line fs-20 text-white"></i>
          </button>
        </div>

        {/* BODY */}
        <div className="p-4" style={{ overflowY: 'auto' }}>
          {/* HEADER LISTA */}
          <div className="d-flex justify-content-between align-items-center">
            <button
                type="button"
                className="btn p-1"
                style={{ background: 'transparent', border: 'none' }}
                onClick={onClose}
                aria-label="Cerrar"
            >
              <i className="ri-arrow-left-line fs-20 text-primary"></i>
            </button>
            <div className="text-muted small fw-medium">Lista de plantillas</div>

            <button
              type="button"
              className="btn bg bg-primary btn-sm text-white d-flex align-items-center gap-1"
              onClick={() => {
                setEditingIndex(null);
                setNewTrigger('');
                setNewResponse('');
                setShowForm(true);
              }}
            >
              <i className="ri-add-line text-white"></i>
              Nueva
            </button>
          </div>

          {/* LISTA */}
          <div style={{ 
            maxHeight: '80vh', 
            overflowY: 'auto',
            scrollbarWidth: 'thin',
            scrollbarColor: '#d1d5db #f9fafb'
          }} 
          className="custom-scrollbar">
            {replies.map((reply, index) => (
              <div
                key={index}
                className="d-flex justify-content-between align-items-start px-3 py-2 rounded-2"
                style={{ cursor: 'pointer', transition: 'background 0.2s' }}
                onMouseEnter={(e) => (e.currentTarget.style.background = '#f8f9fa')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                <div className="flex-grow-1">
                  <div className="fw-medium">/{reply.trigger}</div>
                  <div className="small text-muted">{reply.response}</div>
                </div>

                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="btn bg-warning btn-sm"
                    style={{ background: 'transparent', border: 'none' }}
                    onClick={() => {
                      handleEdit(index);
                      setShowForm(true);
                    }}
                  >
                    <i className="ri-pencil-line text-white"></i>
                  </button>

                  <button
                    type="button"
                    className="btn bg bg-danger btn-sm"
                    style={{ background: 'transparent', border: 'none' }}
                    onClick={() => handleDelete(index)}
                  >
                    <i className="ri-delete-bin-line text-white"></i>
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* FORM EN MODAL REUTILIZABLE */}
          <ModalCreate
            title={editingIndex !== null ? 'Editar una plantilla' : 'Añade una plantilla'}
            show={editingIndex !== null || showForm}
            handle={handleCancel}
            footer={
              <div className="d-flex gap-2 justify-content-end w-100">
                <button type="button" className="btn btn-light" onClick={handleCancel} disabled={loading}>
                  Cancelar
                </button>
                <button
                  type="button"
                  id="add-btn"
                  className="btn btn-primary d-inline-flex align-items-center gap-2"
                  onClick={handleSave}
                  disabled={loading || !newTrigger.trim() || !newResponse.trim()}
                  aria-busy={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                      {editingIndex !== null ? 'Actualizando…' : 'Agregando…'}
                    </>
                  ) : (
                    <>{editingIndex !== null ? 'Actualizar' : 'Agregar'}</>
                  )}
                </button>
              </div>
            }
          >
            <div className="mb-3">
              <Label for="name" className="form-label">
                Acceso directo <span className="text-danger">*</span>
              </Label>

              <div className="position-relative">
                <input
                    type="text"
                    className="form-control pe-5"
                    value={newTrigger}
                    onChange={(e) => setNewTrigger(e.target.value)}
                    placeholder="Atajo (ej: hola, gracias)"
                    maxLength={25}
                />

                <div
                    className="position-absolute text-muted small top-50 translate-middle-y end-0 pe-3 pointer-events-none"
                >
                  {newTrigger.length}/25
                </div>
              </div>
            </div>

            <div className="mb-3">
              <Label for="name" className="form-label">
                Mensaje de respuesta <span className="text-danger">*</span>
              </Label>
              <textarea
                className="form-control"
                value={newResponse}
                onChange={(e) => setNewResponse(e.target.value)}
                placeholder="Escribe la respuesta..."
                rows={3}
              />
            </div>
          </ModalCreate>

        {/* Delete Modal */}
        <DeleteModal
          show={deleteModal}
          onDeleteClick={handleDeleteConfirm}
          onCloseClick={handleDeleteCancel}
          recordId={deleteIndex?.toString()}
          loading={loading}
        />
      </div>
    </div>
    </div>
  );
};

export default QuickRepliesManagerModal;
